"use client";

import { useRef, useState, useEffect, useCallback } from "react";
import Link from "next/link";
import { useRouter } from "next/navigation";
import { Button } from "@/app/components/shadcn/button";
import {
  ChevronLeft,
  ChevronRight,
  X,
  Play,
  Pause,
  Volume2,
  VolumeX,
} from "lucide-react";
import { useStory } from "@/app/lib/features/story/story.hook";
import { cn } from "@/app/utils/cn";
import FallbackImage from "@/app/components/elements/FallbackImage";
import { trackInteraction } from "@/app/lib/tracking/interactions";

interface TouchState {
  startX: number | null;
  startTime: number;
  isDragging: boolean;
}

const LONG_PRESS_DURATION = 350;
const SWIPE_THRESHOLD = 60;
const TAP_DURATION_THRESHOLD = 200;

const StoryContent = () => {
  const {
    isOpen,
    currentStory,
    currentItem,
    progress,
    canGoPrev,
    canGoNext,
    handleNext,
    handlePrev,
    handleClose,
    handlePause,
    handleResume,
    handleStopProgress,
    isPaused,
    isPlaying,
  } = useStory();

  const router = useRouter();

  // Aktif story item'ın link URL'ini önceden prefetch et — tıklandığında sayfa hazır olsun.
  useEffect(() => {
    if (currentItem?.link?.url) {
      router.prefetch(currentItem.link.url);
    }
  }, [router, currentItem?.link?.url]);

  const [isLongPress, setIsLongPress] = useState(false);
  const [isMuted, setIsMuted] = useState(true);

  const isPausedRef = useRef(isPaused);
  useEffect(() => { isPausedRef.current = isPaused; }, [isPaused]);

  const videoRef = useRef<HTMLVideoElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const longPressTimer = useRef<NodeJS.Timeout | null>(null);
  const currentItemIdRef = useRef<number | null>(null);
  const lastTouchEndRef = useRef<number>(0);
  const lastActionRef = useRef<number>(0);
  const touchStateRef = useRef<TouchState>({ startX: null, startTime: 0, isDragging: false });
  const isLongPressRef = useRef(false);

  const clearLongPressTimer = useCallback(() => {
    if (longPressTimer.current) {
      clearTimeout(longPressTimer.current);
      longPressTimer.current = null;
    }
  }, []);

  const startLongPress = useCallback(() => {
    clearLongPressTimer();
    longPressTimer.current = setTimeout(() => {
      isLongPressRef.current = true;
      setIsLongPress(true);
      handlePause();
    }, LONG_PRESS_DURATION);
  }, [handlePause, clearLongPressTimer]);

  const resetTouchState = useCallback(() => {
    touchStateRef.current = { startX: null, startTime: 0, isDragging: false };
  }, []);

  const handleInteractionStart = useCallback((clientX: number) => {
    touchStateRef.current = {
      startX: clientX,
      startTime: performance.now(),
      isDragging: false,
    };
  }, []);

  const resetLongPress = useCallback(() => {
    isLongPressRef.current = false;
    setIsLongPress(false);
  }, []);

  const handleInteractionEnd = useCallback(
    (clientX?: number, isTouch: boolean = false, containerWidth?: number) => {
      clearLongPressTimer();

      const ts = touchStateRef.current;
      const duration = performance.now() - ts.startTime;
      const hasMoved = ts.startX !== null && clientX !== undefined;
      const deltaX = hasMoved ? (ts.startX as number) - clientX : 0;

      if (hasMoved && Math.abs(deltaX) > SWIPE_THRESHOLD) {
        if (deltaX > 0 && canGoNext) {
          handleNext();
        } else if (deltaX < 0 && canGoPrev) {
          handlePrev();
        }
        resetTouchState();
        return;
      }

      if (isLongPressRef.current && isTouch) {
        resetLongPress();
        handleResume();
        resetTouchState();
        return;
      }

      if (duration < TAP_DURATION_THRESHOLD && !ts.isDragging && clientX !== undefined && containerWidth) {
        const now = performance.now();
        if (now - lastActionRef.current < 400) {
          resetTouchState();
          return;
        }
        lastActionRef.current = now;

        if (isPausedRef.current) {
          handleResume();
          resetTouchState();
          return;
        }

        const isRightSide = clientX > containerWidth / 2;
        if (isRightSide) {
          if (canGoNext) handleNext();
        } else {
          if (canGoPrev) handlePrev();
        }
      }

      resetTouchState();
    },
    [
      canGoNext,
      canGoPrev,
      handleNext,
      handlePrev,
      handleResume,
      clearLongPressTimer,
      resetTouchState,
      resetLongPress,
    ],
  );

  const handleTouchStart = (e: React.TouchEvent) => {
    handleInteractionStart(e.touches[0].clientX);
    startLongPress();
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    if (touchStateRef.current.startX !== null) {
      const deltaX = Math.abs(touchStateRef.current.startX - e.touches[0].clientX);
      if (deltaX > 10) {
        touchStateRef.current = { ...touchStateRef.current, isDragging: true };
        clearLongPressTimer();
      }
    }
  };

  const handleTouchEnd = (e: React.TouchEvent) => {
    lastTouchEndRef.current = performance.now();
    const width = containerRef.current?.offsetWidth;
    handleInteractionEnd(e.changedTouches[0].clientX, true, width);
  };

  const handleMouseDown = (e: React.MouseEvent) => {
    if (e.button === 0) {
      handleInteractionStart(e.clientX);
    }
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (touchStateRef.current.startX !== null) {
      const deltaX = Math.abs(touchStateRef.current.startX - e.clientX);
      if (deltaX > 10) {
        touchStateRef.current = { ...touchStateRef.current, isDragging: true };
      }
    }
  };

  const togglePlayPause = useCallback(() => {
    if (isPlaying) {
      handlePause();
    } else {
      handleResume();
    }
  }, [isPlaying, handlePause, handleResume]);

  const handleMouseUp = useCallback(() => {
    const now = performance.now();
    if (now - lastTouchEndRef.current < 500) return;
    const ts = touchStateRef.current;
    const duration = now - ts.startTime;
    if (ts.isDragging) {
      resetTouchState();
      return;
    }
    if (duration < TAP_DURATION_THRESHOLD) {
      togglePlayPause();
    }
    resetTouchState();
  }, [resetTouchState, togglePlayPause]);

  const handleMouseLeave = () => {
    resetTouchState();
  };

  const toggleMute = useCallback(() => {
    setIsMuted((prev) => !prev);
  }, []);

  useEffect(() => {
    if (videoRef.current) {
      videoRef.current.muted = isMuted;
    }
  }, [isMuted]);

  useEffect(() => {
    return () => {
      clearLongPressTimer();
    };
  }, [clearLongPressTimer]);

  useEffect(() => {
    if (videoRef.current && currentItem?.type === "video") {
      if (isPlaying && !isPaused) {
        videoRef.current.play().catch(() => {});
      } else {
        videoRef.current.pause();
      }
    }
  }, [isPlaying, isPaused, currentItem]);

  // Reset long press when item changes - use layout effect to avoid cascade
  useEffect(() => {
    const itemId = currentItem?.id ?? null;
    const hasChanged = itemId !== null && itemId !== currentItemIdRef.current;

    if (hasChanged) {
      currentItemIdRef.current = itemId;
      // Use queueMicrotask to defer setState and avoid ESLint warning
      queueMicrotask(() => {
        isLongPressRef.current = false;
        setIsLongPress(false);
      });
    }
  }, [currentItem?.id]);

  if (!isOpen || !currentStory || !currentItem) return null;

  const isEffectivelyPaused = isPaused || isLongPress;

  return (
    <div className="fixed inset-0 z-999 flex items-center justify-center bg-black/30 m-0">
      <div
        className={cn(
          "relative bg-black overflow-hidden",
          "w-full h-full",
          "md:max-w-100 md:max-h-177.75 md:rounded-2xl md:shadow-2xl",
        )}
      >
        <div className="absolute top-4 left-4 right-4 z-50" onTouchStart={(e) => e.stopPropagation()} onTouchEnd={(e) => e.stopPropagation()}>
          <div className="flex space-x-1">
            {currentStory.items.map((_, i) => (
              <div
                key={i}
                className="flex-1 h-1 bg-white/30 rounded-full overflow-hidden"
              >
                <div
                  className={cn(
                    "h-full bg-white transition-all duration-100 ease-linear",
                    isEffectivelyPaused && "animate-pulse",
                  )}
                  style={{
                    width:
                      i < currentStory.items.indexOf(currentItem)
                        ? "100%"
                        : i === currentStory.items.indexOf(currentItem)
                          ? `${progress}%`
                          : "0%",
                  }}
                />
              </div>
            ))}
          </div>

          <div className="flex items-center justify-between mt-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full overflow-hidden border-2 border-white">
                <FallbackImage
                  src={currentStory.cover_url}
                  alt=""
                  width={40}
                  height={40}
                  className="object-cover"
                  fallbackClassName="object-contain scale-75 opacity-90"
                />
              </div>
              <span className="text-white font-medium">
                {currentStory.title}
              </span>
            </div>

            <div className="flex items-center gap-3">
              <Button
                size="icon-sm"
                variant="outline"
                onClick={togglePlayPause}
                className="rounded-full"
              >
                {isEffectivelyPaused ? <Play size={18} /> : <Pause size={18} />}
              </Button>
              <Button
                size="icon-sm"
                variant="outline"
                onClick={handleClose}
                className="rounded-full"
              >
                <X size={18} />
              </Button>
            </div>
          </div>
        </div>

        <div
          ref={containerRef}
          className="relative w-full h-full md:cursor-default cursor-pointer select-none"
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
          onTouchCancel={handleTouchEnd}
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          onMouseLeave={handleMouseLeave}
        >
          {currentItem.type === "image" ? (
            <FallbackImage
              src={currentItem.image_url}
              alt={currentItem.title}
              fill
              className="object-cover"
              fallbackClassName="object-contain opacity-60"
              priority
            />
          ) : (
            <video
              ref={videoRef}
              src={currentItem.image_url}
              className="w-full h-full object-cover"
              muted
              playsInline
              onEnded={handleNext}
            />
          )}

          {isEffectivelyPaused && (
            <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
              <div className="bg-black/30 rounded-full p-4">
                <Play className="w-8 h-8 text-white" />
              </div>
            </div>
          )}

          {currentItem.type === "video" && (
            <div className="absolute bottom-20 right-4 z-30">
              <Button
                size="icon-sm"
                variant="ghost"
                onClick={(e) => { e.stopPropagation(); toggleMute(); }}
                onMouseDown={(e) => e.stopPropagation()}
                onMouseUp={(e) => e.stopPropagation()}
                onTouchStart={(e) => e.stopPropagation()}
                onTouchEnd={(e) => { e.stopPropagation(); e.preventDefault(); toggleMute(); }}
                className="bg-black/50 hover:bg-black/70 text-white rounded-full"
              >
                {isMuted ? <VolumeX size={18} /> : <Volume2 size={18} />}
              </Button>
            </div>
          )}
        </div>

        <div className="absolute bottom-8 left-4 right-4 z-20 text-center px-4" onTouchStart={(e) => e.stopPropagation()} onTouchEnd={(e) => e.stopPropagation()}>
          <h3 className="text-white text-xl font-bold mb-2">
            {currentItem.title}
          </h3>
          {currentItem.button_text && currentItem.link?.url && (
            <Button variant="outline" className="mt-4" asChild>
              <Link
                href={currentItem.link.url}
                onClick={() => { trackInteraction("stories", currentItem.id, "click"); handleStopProgress(); }}
                onTouchStart={(e) => e.stopPropagation()}
                onTouchEnd={(e) => { e.stopPropagation(); trackInteraction("stories", currentItem.id, "click"); handleStopProgress(); }}
              >
                {currentItem.button_text}
              </Link>
            </Button>
          )}
        </div>

        <div className="absolute inset-0 hidden md:flex items-center justify-between px-6 pointer-events-none">
          <Button
            size="icon"
            variant="outline"
            onClick={handlePrev}
            disabled={!canGoPrev}
            className={cn(
              "pointer-events-auto rounded-full transition-opacity",
              !canGoPrev && "opacity-0 pointer-events-none",
            )}
          >
            <ChevronLeft size={24} />
          </Button>
          <Button
            size="icon"
            variant="outline"
            onClick={handleNext}
            disabled={!canGoNext}
            className={cn(
              "pointer-events-auto rounded-full transition-opacity",
              !canGoNext && "opacity-0 pointer-events-none",
            )}
          >
            <ChevronRight size={24} />
          </Button>
        </div>
      </div>
    </div>
  );
};

export default StoryContent;